import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * Featured Landing Page strip — pulls live, featured LPs and renders as cards.
 * Reuses existing site design system. Rendered inside /offers and /rooms hero areas.
 */
export default function FeaturedLPStrip({ heading = 'Live Campaigns', subheading = 'Ongoing offers — book direct for the best rates' }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/content/active-promotions`)
      .then(r => setItems(r.data?.featured || []))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  return (
    <section className="py-10 sm:py-14 px-4 sm:px-6 lg:px-10 bg-gradient-to-b from-[#1a1f2e] to-[#0F1419]" data-testid="featured-lp-strip">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-4 h-4 text-[#C9A961]" />
          <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961]">{heading}</div>
        </div>
        {subheading && <p className="text-sm text-[#E8E4DC]/60 mb-6">{subheading}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map(lp => (
            <Link
              key={lp.slug}
              to={`/lp/${lp.slug}`}
              className="group border border-[#C9A961]/20 hover:border-[#C9A961]/50 bg-[#0F1419] overflow-hidden transition-all flex flex-col"
              data-testid={`featured-lp-card-${lp.slug}`}
            >
              {lp.hero_image && (
                <div className="h-40 overflow-hidden">
                  <img src={resolveImg(lp.hero_image)} alt={lp.campaign_name} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                {lp.offer_section?.highlight && (
                  <div className="inline-block self-start mb-2 px-2 py-0.5 border border-[#C9A961]/40 text-[#C9A961] text-[10px] tracking-[0.2em] uppercase">
                    {lp.offer_section.highlight}
                  </div>
                )}
                <h3 className="text-lg font-serif text-[#E8E4DC] mb-2">{lp.headline || lp.campaign_name}</h3>
                {lp.subheadline && <p className="text-sm text-[#E8E4DC]/60 mb-4 flex-1">{lp.subheadline}</p>}
                {lp.promo_code && (
                  <div className="text-[11px] text-[#C9A961]/70 mb-3">
                    Promo code: <span className="font-semibold tracking-wider">{lp.promo_code}</span>
                  </div>
                )}
                <div className="inline-flex items-center gap-1 text-xs tracking-[0.15em] uppercase text-[#C9A961] group-hover:text-[#D4B76E] mt-auto">
                  {lp.cta_text || 'View Offer'} <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
